import { ChangeDetectionStrategy, Component, computed, effect, inject, input, output, signal, untracked } from '@angular/core';
import { CurrencyPipe } from '@angular/common';
import { take } from 'rxjs';
import {
  BriefApplicationResponseDto,
  BriefResponseDto,
  SocialChannelEnum,
} from '../../../../common/general-types/briefs.dto';
import { socialChannelIdsOnPieces } from '@/app/common/brief-pieces/piece-rules';
import { CreatorResponseDto } from '../../../../common/general-types/creators.dto';
import { PaymentMethodTypeEnum } from '../../../../common/general-types/shout-plus.dto';
import { WalletTransactionResponseDto } from '../../../../common/general-types/wallet-transaction.dto';
import {
  SubmittedPostDto,
  SubmittedPostStatusEnum,
} from '../../../../common/general-types/submitted-posts.dto';
import { SubmittedPostsService } from '../../../../services/submitted-posts/submitted-posts.service';
import { PricingModelV2Service } from '../../../../services/pricing-model-v2/pricing-model-v2.service';
import {
  ChannelFollowerOverlay,
  resolveChannelFollowers,
} from '../../../../services/pricing-model-v2/channel-followers.util';
import { UltraSocialService } from '../../../../services/ultra-social/ultra-social';
import { WalletService } from '../../../../services/wallet/wallet.service';
import { BriefApplicationService } from '../../../../services/brief-application/brief-application.service';
import { ToastNotificationService } from '../../../../atoms/toast-notification/toast-notification.service';
import { AlertService } from '../../../../atoms/alert/alert.service';
import { CrossPlatformTile } from '../../../manage-brief-tabs/tabs/assignment/atoms/cross-platform-tile/cross-platform-tile';
import {
  briefCrossChannels,
  postStatusDetail,
  readChannels,
  type CrossPlatformState,
} from '../../../manage-brief-tabs/tabs/assignment/cross-platform-state.util';
import { getSocialChannelLabel, creatorHasPlatform } from '../../../insights/tabs/creator/molecules/application-drawer/molecules/application-actions/application-actions.helpers';
import {
  getResolvedPaymentMethodType,
  getShoutPlusCashPrice,
  isShoutPlusBrief,
  isShoutPlusNonCashBrief,
} from '../../../../shout-plus/utils/shout-plus-hire.util';
import { shouldApplyVat } from '../../../manage-brief-tabs/tabs/assignment/hire-cost.util';
import {
  breakdownFromReservedTotal,
  channelContributesToTotal,
  walletRefreshAccountId,
  committedFromReservations,
  displayedHireBreakdown,
  distributionStatusPill,
  priceForDistributionRow,
  reservationForChannel,
  type DistributionStatusPill,
} from './creator-distribution.helpers';

/** One platform row on the distribution card. */
export interface DistributionRow {
  socialChannel: number;
  label: string;
  /** Primary / Cross-post on a legacy brief. Placement on a piece-list hire. */
  roleLabel: string;
  isPrimary: boolean;
  state: CrossPlatformState;
  /**
   * Brand cost of this channel for this creator, matching the Review application modal:
   * the whole primary fee on the required network, and the audience bonus only on a
   * hired cross-post.
   *
   * Null means we cannot quote it — unknown followers, or a cross-post that is not in
   * the hire — which is not the same as a genuine zero and must not be rendered as one.
   */
  price: number | null;
  /** True when a reservation exists for this channel, rather than a live estimate. */
  priceIsCommitted: boolean;
  /** Populated when the creator has posted here. */
  post?: SubmittedPostDto;
  detail: string;
  /** Cancel is offered only on an assigned cross-post with no submitted post yet. */
  canCancel: boolean;
  /** Hire-intent fallback, or the submitted-post verification status when one exists. */
  statusPill: DistributionStatusPill;
}

/**
 * "Where this creator has posted" — one creator's distribution across every channel
 * the brief covers.
 *
 * Renders a row for EVERY channel on the brief, including ones the creator has no
 * account for: a missing row would be a missing decision. Read-only apart from
 * cancelling a cross-post the brand committed to but the creator has not delivered,
 * which releases the money reserved for it.
 */
@Component({
  selector: 'vs-creator-distribution',
  imports: [CurrencyPipe, CrossPlatformTile],
  templateUrl: './creator-distribution.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
  host: { class: 'flex h-full min-h-0 flex-col' },
})
export class CreatorDistribution {
  readonly brief = input.required<BriefResponseDto | undefined>();
  readonly application = input.required<BriefApplicationResponseDto | undefined>();

  /**
   * The creator this card is about.
   *
   * Supplied by the page rather than read off the application: the endpoint behind
   * `focussedApplication` (`student-application-with-brief`) does not load the `creator`
   * relation, so `application.creator` is undefined here and every row priced at zero.
   * The page already holds the creator for the Payments card next to this one.
   */
  readonly creator = input<CreatorResponseDto | undefined>();

  /** Emitted after a cancel so the host can refresh its own view of the application. */
  readonly cancelled = output<number>();

  private submittedPostsService = inject(SubmittedPostsService);
  private pricingModelService = inject(PricingModelV2Service);
  private briefApplicationService = inject(BriefApplicationService);
  private toastNotificationService = inject(ToastNotificationService);
  private alertService = inject(AlertService);
  private ultraSocialService = inject(UltraSocialService);
  private walletService = inject(WalletService);

  readonly posts = signal<SubmittedPostDto[]>([]);
  /** Reservations for this hire — the money that actually moved. */
  readonly reservations = signal<WalletTransactionResponseDto[]>([]);
  /**
   * Follower counts the API resolved, keyed by creator so a switch of subject cannot
   * inherit the previous one's numbers.
   */
  private readonly followerOverlay = signal<{
    uid: string;
    followers: ChannelFollowerOverlay;
  } | null>(null);
  private readonly followersRequested = new Set<string>();
  readonly loading = signal<boolean>(false);
  readonly cancelling = signal<number | null>(null);
  /** Channels cancelled in this session, so the card updates without a reload. */
  private readonly locallyCancelled = signal<number[]>([]);

  readonly currencyCode = computed(() =>
    (this.brief()?.currentPrice?.currencyCode ?? 'GBP').toUpperCase(),
  );

  /** Shown in the header; the application no longer carries the creator. */
  readonly creatorFirstName = computed(() => this.subject()?.firstName ?? 'this creator');

  /** Prefer the creator the page passed in; fall back to the application's own. */
  private readonly subject = computed<CreatorResponseDto | undefined>(
    () => this.creator() ?? this.application()?.creator,
  );

  /** The overlay, but only while it still belongs to the creator on screen. */
  private readonly activeOverlay = computed<ChannelFollowerOverlay | null>(() => {
    const overlay = this.followerOverlay();
    const uid = this.subject()?.blazeFirebaseUid;
    return overlay && uid && overlay.uid === uid ? overlay.followers : null;
  });

  /** Surface classes for one channel cell, including the stronger primary treatment. */
  cellSurfaceClass(row: DistributionRow): string {
    const muted = row.state === 'none' || row.state === 'locked' ? ' opacity-60' : '';
    const tone = row.isPrimary
      ? 'bg-violet-50 ring-violet-200 dark:bg-violet-500/10 dark:ring-violet-400/30'
      : 'bg-zinc-50 ring-zinc-200/80 dark:bg-zinc-900/55 dark:ring-white/10';
    return `flex flex-col gap-y-2 rounded-lg p-2.5 ring-1 ring-inset ${tone}${muted}`;
  }

  /** Title on an em dash, only when the missing figure is a missing rate. */
  unpricedTitle(row: DistributionRow): string {
    if (!channelContributesToTotal(row.isPrimary, row.state)) {
      return '';
    }
    return 'No follower count for this channel, so there is no rate to quote';
  }

  /** True when the hire has channels that can still be dropped. */
  readonly isVisible = computed(() => {
    const brief = this.brief();
    if ((brief?.pieces?.length ?? 0) > 0) {
      return socialChannelIdsOnPieces(brief?.pieces ?? []).length > 0;
    }
    const shoutBrief = brief?.shoutBrief;
    return !!shoutBrief && briefCrossChannels(shoutBrief).length > 0;
  });

  readonly rows = computed<DistributionRow[]>(() => {
    const brief = this.brief();
    const application = this.application();
    if (!brief || !application) {
      return [];
    }
    if (brief.pieces?.length) {
      return this.pieceRows(brief, application);
    }
    const shoutBrief = brief.shoutBrief;
    if (!shoutBrief) {
      return [];
    }

    const primary = Number(shoutBrief.socialPlatform);
    const accepted = readChannels(application.crossPlatformsAccepted).filter(
      (channel) => !this.locallyCancelled().includes(channel),
    );
    const putForward = readChannels(application.crossPlatformsPutForward);

    // Primary first, then every allowed cross-post channel in brief order.
    const creator = this.subject();
    const reservations = this.reservations();

    return [primary, ...briefCrossChannels(shoutBrief)].map((socialChannel) => {
      const isPrimary = socialChannel === primary;
      const post = this.postFor(socialChannel);
      const isLive = post?.status === SubmittedPostStatusEnum.Valid;

      let state: CrossPlatformState;
      if (isLive) {
        state = 'posted';
      } else if (isPrimary) {
        state = 'assigned';
      } else if (accepted.includes(socialChannel)) {
        state = 'assigned';
      } else if (putForward.includes(socialChannel)) {
        state = 'excluded';
      } else if (!creatorHasPlatform(creator, socialChannel)) {
        state = 'none';
      } else {
        state = 'locked';
      }

      const inHire = channelContributesToTotal(isPrimary, state);
      const committed = reservationForChannel(reservations, socialChannel, isPrimary);
      const price = priceForDistributionRow({
        isPrimary,
        inHire,
        primaryFee: this.primaryFee(creator),
        audienceBonus: this.crossRate(socialChannel, creator),
      });

      return {
        socialChannel,
        label: getSocialChannelLabel(socialChannel),
        roleLabel: isPrimary ? 'Primary' : 'Cross-post',
        isPrimary,
        state,
        price,
        priceIsCommitted: committed !== null,
        post,
        detail: this.detailFor(socialChannel, state, isPrimary, post?.status),
        canCancel: !isPrimary && state === 'assigned' && !post,
        statusPill: distributionStatusPill(state, post?.status),
      };
    });
  });

  /** Accepted social placements on a piece-list hire. None of them is a required primary. */
  private pieceRows(
    brief: BriefResponseDto,
    application: BriefApplicationResponseDto,
  ): DistributionRow[] {
    const accepted = readChannels(application.crossPlatformsAccepted).filter(
      (channel) => !this.locallyCancelled().includes(channel),
    );
    const putForward = readChannels(application.crossPlatformsPutForward);
    const creator = this.subject();

    return socialChannelIdsOnPieces(brief.pieces ?? []).map((socialChannel) => {
      const post = this.postFor(socialChannel);
      const isLive = post?.status === SubmittedPostStatusEnum.Valid;
      let state: CrossPlatformState;
      if (isLive) {
        state = 'posted';
      } else if (accepted.includes(socialChannel)) {
        state = 'assigned';
      } else if (putForward.includes(socialChannel)) {
        state = 'excluded';
      } else if (!creatorHasPlatform(creator, socialChannel)) {
        state = 'none';
      } else {
        state = 'locked';
      }
      const inHire = state === 'assigned' || state === 'posted';
      return {
        socialChannel,
        label: getSocialChannelLabel(socialChannel),
        roleLabel: 'Placement',
        isPrimary: false,
        state,
        price: this.crossRate(socialChannel, creator),
        priceIsCommitted: inHire,
        post,
        detail: state === 'assigned' ? 'Included in the hire' : state === 'posted' ? 'Posted' : 'Not on this hire',
        canCancel: state === 'assigned' && !post,
        statusPill: distributionStatusPill(state, post?.status),
      };
    });
  }

  /**
   * Whole primary fee — base, rank and audience bonus across every video — the same
   * figure the Review application modal shows on the required network.
   *
   * SHOUT+ cash briefs pay a fixed amount instead. Null only when we cannot name a
   * creator or a brief to price against.
   */
  private primaryFee(creator: CreatorResponseDto | undefined): number | null {
    const brief = this.brief();
    if (!brief || !creator) {
      return null;
    }
    const paymentMethod = brief.brief_payment_method ?? brief.briefPaymentMethod;
    if (
      isShoutPlusBrief(brief) &&
      getResolvedPaymentMethodType(brief, paymentMethod) === PaymentMethodTypeEnum.CASH &&
      paymentMethod
    ) {
      return getShoutPlusCashPrice(paymentMethod);
    }
    return this.pricingModelService.getTotalPrice(brief, creator, this.activeOverlay());
  }

  /**
   * Audience bonus for one cross-post, matching the modal's put-forward rate.
   *
   * Null when there is no follower count to band against. A SHOUT+ product/voucher
   * brief excludes the bonus outright, which really is zero.
   */
  private crossRate(
    socialChannel: number,
    creator: CreatorResponseDto | undefined,
  ): number | null {
    const brief = this.brief();
    if (!brief || !creator) {
      return null;
    }
    const paymentMethod = brief.brief_payment_method ?? brief.briefPaymentMethod;
    if (isShoutPlusNonCashBrief(brief, paymentMethod)) {
      return 0;
    }
    const overlay = this.activeOverlay();
    if (
      this.pricingModelService.getFollowersForChannel(
        socialChannel as SocialChannelEnum,
        creator,
        overlay,
      ) === null
    ) {
      return null;
    }
    return this.pricingModelService.getAudiencePrice(
      socialChannel as SocialChannelEnum,
      brief,
      creator,
      overlay,
    );
  }

  /**
   * Whether the wallet still holds a reservation for this hire.
   *
   * Drives the "Committed to …" subtitle only. The header figure itself is the
   * VAT-inclusive hire total, matching the Review application modal.
   */
  readonly hasCommittedHire = computed(
    () => committedFromReservations(this.reservations()) !== null,
  );

  /**
   * Header total as the brand is charged it: displayed row prices plus VAT.
   *
   * Same figure as the Review application hire button (`hireCostBreakdown.total`).
   * Row prices stay net; VAT is applied once on the whole hire. Null when nothing
   * can be quoted yet.
   */
  private readonly hireBreakdown = computed(() => {
    const rows = this.rows();
    const brief = this.brief();
    const paymentMethod = brief?.brief_payment_method ?? brief?.briefPaymentMethod;
    const reserved = committedFromReservations(this.reservations());
    if ((brief?.pieces?.length ?? 0) > 0 && reserved !== null) {
      return breakdownFromReservedTotal({
        reserved,
        vatApplies: shouldApplyVat(brief, paymentMethod),
      });
    }
    return displayedHireBreakdown({
      primaryNet: rows.find((row) => row.isPrimary)?.price ?? null,
      crossNets: rows.filter((row) => !row.isPrimary).map((row) => row.price),
      brief,
      paymentMethod,
    });
  });

  readonly committedTotal = computed(() => this.hireBreakdown()?.total ?? null);

  /** Net hire cost, for the "£100 + £20 VAT" caption under the header total. */
  readonly committedNet = computed(() => this.hireBreakdown()?.net ?? null);

  /** VAT on the hire; 0 when the brief is exempt. */
  readonly committedVat = computed(() => this.hireBreakdown()?.vat ?? 0);

  /** The application the loaded posts belong to, so we refetch only when it changes. */
  private loadedFor: string | undefined;

  constructor() {
    // Inputs are not readable in the constructor body, so react to them instead. This
    // also covers the creator being switched without the component being recreated.
    effect(() => {
      const applicationId = this.application()?.id;
      if (applicationId && applicationId !== this.loadedFor) {
        this.loadedFor = applicationId;
        this.loadPosts(applicationId);
        this.loadReservations(applicationId);
      }
    });

    /**
     * Fill in follower counts the creator record cannot supply.
     *
     * Live rates are a lookup on audience size — including for channels that already
     * have a reservation, because the displayed figure is the modal's rate, not the
     * wallet amount. Worth a round trip when a count is missing, but not on every render.
     */
    effect(() => {
      const creator = this.subject();
      const uid = creator?.blazeFirebaseUid;
      const channels = this.rows().map((row) => row.socialChannel);

      if (!creator || !uid || channels.length === 0) {
        return;
      }

      untracked(() => {
        if (this.followersRequested.has(uid)) {
          return;
        }
        const anyUnknown = channels.some(
          (channel) => resolveChannelFollowers(creator, channel) === null,
        );
        if (!anyUnknown) {
          return;
        }

        this.followersRequested.add(uid);
        this.ultraSocialService
          .getCreatorFollowers(uid)
          .pipe(take(1))
          .subscribe({
            next: (response) =>
              this.followerOverlay.set({ uid, followers: response?.followers ?? {} }),
            error: (error: unknown) =>
              console.error('Could not load follower counts', error),
          });
      });
    });
  }

  /**
   * The reservations held for this hire.
   *
   * Failing quietly leaves `hasCommittedHire` false, which reads as "nothing committed
   * yet" rather than as a wrong number.
   */
  private loadReservations(applicationId: string): void {
    this.walletService
      .getTransactionsByApplication(applicationId)
      .pipe(take(1))
      .subscribe({
        next: (transactions) =>
          this.reservations.set((transactions as WalletTransactionResponseDto[]) ?? []),
        error: (error: unknown) => {
          console.error('Could not load wallet reservations', error);
          this.reservations.set([]);
        },
      });
    this.refreshNavbarBalance();
  }

  /**
   * The header balance is fetched when the brand changes, not when money moves.
   * Dropping a placement releases a reservation, so the button has to be asked again.
   */
  private refreshNavbarBalance(): void {
    const businessAccountId = walletRefreshAccountId(this.brief());
    if (!businessAccountId) {
      return;
    }
    this.walletService.getBalance(businessAccountId).pipe(take(1)).subscribe({
      error: (error: unknown) => console.warn('Could not refresh wallet balance', error),
    });
  }

  private loadPosts(applicationId: string): void {
    this.loading.set(true);
    this.submittedPostsService
      .getSubmittedPostsForBriefApplication(applicationId)
      .pipe(take(1))
      .subscribe({
        next: (posts) => {
          this.posts.set(posts ?? []);
          this.loading.set(false);
        },
        error: () => {
          // The card degrades to intent-only rather than disappearing.
          this.posts.set([]);
          this.loading.set(false);
        },
      });
  }

  private postFor(socialChannel: number): SubmittedPostDto | undefined {
    const matches = this.posts().filter((post) => Number(post.socialChannel) === socialChannel);
    return matches.find((post) => post.status === SubmittedPostStatusEnum.Valid) ?? matches[0];
  }

  private detailFor(
    socialChannel: number,
    state: CrossPlatformState,
    isPrimary: boolean,
    postStatus?: SubmittedPostStatusEnum | string,
  ): string {
    const statusDetail = postStatusDetail(postStatus);

    switch (state) {
      case 'posted':
        return isPrimary ? 'Primary post is live' : 'Cross-posted';
      case 'assigned':
        if (statusDetail) {
          return isPrimary
            ? `Required post — ${statusDetail}`
            : `Included in the hire · ${statusDetail}`;
        }
        return isPrimary
          ? 'Required post — not live yet'
          : 'Included in the hire · same asset as the primary';
      case 'excluded':
        return 'Put forward at application, not included in the hire';
      case 'none':
        return `No connected ${getSocialChannelLabel(socialChannel)} account — ask the creator to link one.`;
      default:
        return 'Not put forward';
    }
  }

  async onCancel(row: DistributionRow): Promise<void> {
    const application = this.application();
    if (!application || this.cancelling() !== null) {
      return;
    }

    const pieceHire = (this.brief()?.pieces?.length ?? 0) > 0;
    const confirmed = await this.alertService.confirm({
      title: pieceHire ? `Drop the ${row.label} placement?` : `Cancel the ${row.label} cross-post?`,
      message: pieceHire
        ? 'The creator will no longer owe this placement, and the money reserved for it goes back to your wallet.'
        : 'The creator will no longer be expected to post here, and the fee reserved for it is released back to your wallet.',
      type: 'confirm',
      icon: 'warning',
      confirmText: pieceHire ? 'Drop placement' : 'Cancel cross-post',
      cancelText: 'Keep it',
    });
    if (!confirmed) {
      return;
    }

    this.cancelling.set(row.socialChannel);
    this.briefApplicationService
      .cancelCrossPlatform(application.id, row.socialChannel)
      .pipe(take(1))
      .subscribe({
        next: () => {
          this.locallyCancelled.update((channels) => [...channels, row.socialChannel]);
          this.cancelling.set(null);
          this.cancelled.emit(row.socialChannel);
          // Cancelling soft-deletes the reservation; without this the "committed"
          // subtitle would keep claiming the released fee until the page is reloaded.
          this.loadReservations(application.id);
          void this.toastNotificationService.alert({
            title: (this.brief()?.pieces?.length ?? 0) > 0 ? 'Placement dropped' : 'Cross-post cancelled',
            message: (this.brief()?.pieces?.length ?? 0) > 0
              ? `${row.label} dropped — reserved funds returned`
              : `${row.label} cross-post cancelled — fee released`,
            type: 'success',
            icon: 'success',
          });
        },
        error: () => {
          this.cancelling.set(null);
          void this.toastNotificationService.alert({
            title: 'Could not cancel',
            message: `The ${row.label} cross-post could not be cancelled. It may already be live.`,
            type: 'error',
            icon: 'error',
          });
        },
      });
  }
}
